import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import messages from './messages';
import type { ThinkingStep } from '../types';
import { isFinished } from '../utils/messageParts';

const MIN_DWELL_MS = 900;

const getLatestRunningStep = (steps: ThinkingStep[]) =>
  steps.findLast(step => !isFinished(step));

const getGapMessage = (steps: ThinkingStep[]) =>
  steps.length ? messages.reviewingResults : messages.readingQuestion;

/**
 * The line the thinking headline shows while a turn runs: the latest running step's
 * label, else "reading your question" before any step and "reviewing" after them.
 * Each line stays up for at least MIN_DWELL_MS so fast tools don't flash past.
 */
export const useProgressLabel = (steps: ThinkingStep[]): string => {
  const { formatMessage } = useIntl();
  const running = getLatestRunningStep(steps);
  const target = running
    ? `${running.label}…`
    : formatMessage(getGapMessage(steps));

  const [shown, setShown] = useState(target);
  const shownAt = useRef(Date.now());

  useEffect(() => {
    if (target === shown) return undefined;
    const wait = Math.max(0, MIN_DWELL_MS - (Date.now() - shownAt.current));
    const timer = setTimeout(() => {
      shownAt.current = Date.now();
      setShown(target);
    }, wait);
    return () => clearTimeout(timer);
  }, [target, shown]);

  return shown;
};
